import React, { useState, useEffect, useContext, useMemo } from 'react';
import styles from './ManagePage.module.css';
import AuthContext from '../../context/AuthContext';
import SingleResultsCard from '../../components/Generator/SingleResultsCard';
import BulkResultsCard from '../../components/Generator/BulkResultsCard';
import ManageHeader from './components/ManageHeader';
import SingleProjectCard from './components/SingleProjectCard';
import BulkProjectCard from './components/BulkProjectCard';
import ListingScoreSection from './components/ListingScoreSection';
import DealTagSection from './components/DealTagSection';
import SingleScoreCard from './components/SingleScoreCard';
import BulkScoreCard from './components/BulkScoreCard';
import DealTagCard from './components/DealTagCard';
import TitleUpdateCard from './components/TitleUpdateCard';
import TitleUpdateSection from './components/TitleUpdateSection';
import DealTagValidation from '../Tools/DealTagValidation';
import UpdateTitleResultsPage from '../UpdateTitle/UpdateTitleResultsPage';
import useListingScoreHistory from './hooks/useListingScoreHistory';
import useDealTagHistory from './hooks/useDealTagHistory';
import useTitleUpdateHistory from './hooks/useTitleUpdateHistory';
import { viewListingScore, viewScoreBatch } from './utils/scoreNavigation';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import PaginationControls from './components/PaginationControls';
import { useSearch } from './hooks/useSearch';
import { useFilterPanels } from './hooks/useFilterPanels';
import { useFilters } from './hooks/useFilters';
import { useSort } from './hooks/useSort';
import { useProjectTypeFilter, ALL_PROJECT_TYPES, PROJECT_TYPE_LABELS } from './hooks/useProjectTypeFilter';
import useManageData from './hooks/useManageData';
import useFilteredProjects from './hooks/useFilteredProjects';
import useProjectSelection from './hooks/useProjectSelection';
import useProjectNameEdit from './hooks/useProjectNameEdit';
import { FaSpinner } from 'react-icons/fa';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  isDraftItem, getListingMode, getCardTitle, getBulkProjectName,
  getCardImage, getBulkPreviewImage, transformGenerationData, formatDate
} from './utils/helpers';
import { makeAuthenticatedRequest } from './utils/apiHelpers';

const ITEMS_PER_PAGE = 20;

const ManagePage = () => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { project_id } = useParams();

  // Header hooks
  const searchHook = useSearch('');
  const filterPanelsHook = useFilterPanels();
  const filtersHook = useFilters(['draft', 'completed'], []);
  const sortHook = useSort('date-desc');
  const projectTypeFilterHook = useProjectTypeFilter();

  const { searchTerm } = searchHook;
  const { selectedStatuses, selectedCategories, selectedModes } = filtersHook;
  const { selectedSortOption } = sortHook;
  const { selectedProjectTypes } = projectTypeFilterHook;
  // Empty selection = default view: no button highlighted, every type shown
  const effectiveProjectTypes = selectedProjectTypes.length ? selectedProjectTypes : ALL_PROJECT_TYPES;

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Data
  const data = useManageData({
    token: authContext.token,
    isLoggedIn: authContext.isLoggedIn,
    projectId: project_id,
    navigate,
    location,
    onLogout: () => selection.clearAll(),
  });

  // Filtering
  const { filteredGenerations, filteredBulkGenerations, allFilteredProjects, getUniqueCategories } =
    useFilteredProjects({
      generations: data.generations,
      bulkGenerations: data.bulkGenerations,
      searchTerm, selectedStatuses, selectedCategories, selectedModes, selectedSortOption,
      selectedProjectTypes: effectiveProjectTypes,
    });

  // Selection & deletion
  const selection = useProjectSelection({
    token: authContext.token,
    selectedProjectTypes: effectiveProjectTypes,
    filteredGenerations,
    filteredBulkGenerations,
    allFilteredProjects,
    selectedBulkProject: data.selectedBulkProject,
    setGenerations: data.setGenerations,
    setBulkGenerations: data.setBulkGenerations,
    setSelectedBulkProject: data.setSelectedBulkProject,
    setBulkExcelData: data.setBulkExcelData,
  });

  // Project name editing
  const nameEdit = useProjectNameEdit({
    token: authContext.token,
    setGenerations: data.setGenerations,
    setBulkGenerations: data.setBulkGenerations,
  });

  // Listing-score & deal-tag history, shared with the filter-view sections
  const scoreHistory = useListingScoreHistory(authContext.token);
  const dealHistory = useDealTagHistory(authContext.token);
  const titleHistory = useTitleUpdateHistory(authContext.token);
  const [selectedDealBatch, setSelectedDealBatch] = useState(null);
  // Title-update run opened inline (renders the results view instead of the lists).
  const [selectedTitleProject, setSelectedTitleProject] = useState(null);

  // Default view (nothing selected) interleaves every card type
  const isDefaultView = selectedProjectTypes.length === 0;

  // Search/status/category/mode filters only describe generation projects, so
  // score and deal-tag history cards hide whenever any of them is active.
  // Statuses are neutral with 2+ selected (filterByStatus shows all items then).
  const contentFiltersActive = Boolean(searchTerm)
    || selectedStatuses.length < 2
    || selectedCategories.length > 0
    || selectedModes.length > 0;

  // One merged stream: generation projects plus (on the default view) score and
  // deal-tag history, ordered by creation date. When a non-date sort is chosen,
  // the generation projects keep that order and history items follow by date.
  const mergedItems = useMemo(() => {
    const genItems = allFilteredProjects.map((project) => ({
      type: 'gen',
      key: `gen-${project._id}`,
      date: project.created_at ? new Date(project.created_at) : new Date(0),
      project,
    }));
    if (!isDefaultView || contentFiltersActive) return genItems;
    const historyItems = [
      ...scoreHistory.combinedItems.map((item) => ({ ...item, type: `score-${item.type}` })),
      ...dealHistory.batches.map((batch) => ({
        type: 'deal',
        key: `deal-${batch.task_id}`,
        date: batch.date ? new Date(batch.date) : new Date(0),
        batch,
      })),
      ...titleHistory.projects.map((project) => ({
        type: 'title',
        key: `title-${project._id}`,
        date: project.created_at ? new Date(project.created_at) : new Date(0),
        project,
      })),
    ];
    const dir = selectedSortOption === 'date-asc' ? 1 : -1;
    const byDate = (a, b) => dir * (a.date - b.date);
    if (selectedSortOption === 'date-asc' || selectedSortOption === 'date-desc') {
      return [...genItems, ...historyItems].sort(byDate);
    }
    return [...genItems, ...historyItems.sort(byDate)];
  }, [allFilteredProjects, isDefaultView, contentFiltersActive, scoreHistory.combinedItems, dealHistory.batches, titleHistory.projects, selectedSortOption]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatuses, selectedCategories, selectedModes, selectedSortOption, selectedProjectTypes]);

  // Apply a preselected type filter when directed from tool pages
  // (Deal Tag "History", Listing Score "Bulk Results") or the navbar's Task History.
  useEffect(() => {
    const preset = location.state?.projectTypeFilter;
    if (preset && ALL_PROJECT_TYPES.includes(preset)) {
      projectTypeFilterHook.setSelectedProjectTypes([preset]);
      // The page may already be showing an inline result view (this route stays
      // mounted), so drop back to the list or the filter would look ignored.
      data.setShowResults(false);
      data.setSelectedGeneration(null);
      data.setShowBulkResults(false);
      data.setSelectedBulkProject(null);
      data.setBulkExcelData(null);
      setSelectedDealBatch(null);
      setSelectedTitleProject(null);
      // Clear the state so refreshing or re-navigating doesn't re-apply it
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.projectTypeFilter]);

  // Clear all filters
  const clearAllFilters = () => {
    searchHook.clearSearch();
    filtersHook.clearAllFilters();
    sortHook.setSelectedSortOption('date-desc');
    projectTypeFilterHook.setSelectedProjectTypes([]);
  };

  // Draft click handler
  const handleDraftClick = (draft) => {
    if (draft.request_type === 'bulk_draft') {
      navigate('/dashboard/generate/bulk', {
        state: { loadBulkDraft: true, draftData: draft, isNewListing: draft.listing_mode !== 'existing' }
      });
      return;
    }
    navigate('/dashboard/generate/single', {
      state: { loadDraft: true, draftData: draft, isNewListing: draft.listing_mode !== 'existing' }
    });
  };

  // Card click handlers
  const handleCardClick = (generation) => {
    if (selection.isSelectionMode) return;
    if (isDraftItem(generation)) { handleDraftClick(generation); return; }
    data.setSelectedGeneration(transformGenerationData(generation));
    data.setShowResults(true);
  };

  const handleBulkCardClick = async (bulkProject) => {
    if (selection.isBulkSelectionMode) return;
    if (isDraftItem(bulkProject)) { handleDraftClick(bulkProject); return; }
    if (bulkProject.image_urls) {
      data.loadBulkExcelData(bulkProject);
      data.setSelectedBulkProject(bulkProject);
      data.setShowBulkResults(true);
      return;
    }
    // Slim mode: fetch full project data
    try {
      const url = `${process.env.REACT_APP_API_URL}/auth/user_history/${bulkProject._id}`;
      const response = await makeAuthenticatedRequest(url, authContext.token);
      if (response.data?.data) {
        const fullProject = response.data.data;
        data.setBulkGenerations(prev => prev.map(p => p._id === fullProject._id ? fullProject : p));
        data.loadBulkExcelData(fullProject);
        data.setSelectedBulkProject(fullProject);
      } else {
        data.setSelectedBulkProject(bulkProject);
      }
    } catch {
      data.setSelectedBulkProject(bulkProject);
    }
    data.setShowBulkResults(true);
  };

  const handleCloseResults = () => { data.setShowResults(false); data.setSelectedGeneration(null); };
  const handleCloseBulkResults = () => {
    data.setShowBulkResults(false); data.setSelectedBulkProject(null); data.setBulkExcelData(null);
  };

  // Pagination
  const getPaginatedData = (items) => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return items.slice(start, start + ITEMS_PER_PAGE);
  };

  // Computed
  const isInSelectionMode = (type) => {
    if (type === 'single') return selection.isSelectionMode || (selection.isBulkSelectionMode && effectiveProjectTypes.includes('single') && effectiveProjectTypes.includes('bulk'));
    return selection.isBulkSelectionMode || (selection.isSelectionMode && effectiveProjectTypes.includes('single') && effectiveProjectTypes.includes('bulk'));
  };

  // --- Render ---

  if (data.loading) {
    return <div className={styles.container}><div className={styles.loading}><FaSpinner className={styles.spinner} /></div></div>;
  }
  if (data.error) {
    return <div className={styles.container}><div className={styles.error}>{data.error}</div></div>;
  }
  if (data.showBulkResults && data.selectedBulkProject) {
    return (
      <div className={styles.container}>
        <BulkResultsCard onClose={handleCloseBulkResults} bulkProjectData={data.selectedBulkProject}
          preTransformedData={data.bulkExcelData} isDataLoading={data.bulkDataLoading} />
      </div>
    );
  }
  if (data.showResults && data.selectedGeneration) {
    return (
      <div className={styles.container}>
        <SingleResultsCard resultData={data.selectedGeneration} onClose={handleCloseResults}
          isInline={true} showFormButton={false} onContentUpdated={data.handleContentUpdated}
          authToken={authContext.token} />
      </div>
    );
  }
  if (selectedDealBatch) {
    return (
      <div className={styles.container}>
        <DealTagValidation key={selectedDealBatch.task_id} batch={selectedDealBatch}
          onBack={() => setSelectedDealBatch(null)} />
      </div>
    );
  }
  if (selectedTitleProject) {
    return (
      <div className={styles.container}>
        <UpdateTitleResultsPage results={selectedTitleProject.results}
          onBack={() => setSelectedTitleProject(null)} />
      </div>
    );
  }

  const totalSelected = selection.selectedProjects.length + selection.selectedBulkProjects.length;
  const showProjectsList = effectiveProjectTypes.includes('single') || effectiveProjectTypes.includes('bulk');
  const hasNoProjects = data.generations.length === 0 && data.bulkGenerations.length === 0
    && (!isDefaultView || mergedItems.length === 0);
  const hasNoFilteredProjects = mergedItems.length === 0;
  // Header title mirrors the nav button's label when a single type is selected
  const listTitle = (!isDefaultView && selectedProjectTypes.length === 1 && PROJECT_TYPE_LABELS[selectedProjectTypes[0]])
    || 'All Projects';

  return (
    <>
      {selection.showDeleteConfirm && (
        <DeleteConfirmModal count={totalSelected} isDeleting={selection.isDeleting}
          onCancel={() => selection.setShowDeleteConfirm(false)} onConfirm={selection.handleConfirmedDelete} />
      )}

      <div className={styles.container}>
        <ManageHeader searchHook={searchHook} filterPanelsHook={filterPanelsHook}
          filtersHook={filtersHook} sortHook={sortHook}
          projectTypeFilterHook={projectTypeFilterHook} getUniqueCategories={getUniqueCategories} />

        {!isDefaultView && selectedProjectTypes.includes('listing_score') && (
          <ListingScoreSection data={scoreHistory} />
        )}

        {!isDefaultView && selectedProjectTypes.includes('deal_tag') && (
          <DealTagSection data={dealHistory} />
        )}

        {!isDefaultView && selectedProjectTypes.includes('update_title') && (
          <TitleUpdateSection data={titleHistory} onOpen={setSelectedTitleProject} />
        )}

        {showProjectsList && (hasNoProjects || hasNoFilteredProjects) && (
          <div className={styles.emptyState}>
            {hasNoProjects ? (
              <>
                <h3>No projects found</h3>
                <p>You haven't created any projects yet. Create your first project to see it here.</p>
              </>
            ) : (
              <>
                <h3>No projects match your filters</h3>
                <p>Try adjusting your search terms or filters to see more results.</p>
                <button onClick={clearAllFilters} className={styles.clearFiltersButton}>Clear All Filters</button>
              </>
            )}
          </div>
        )}

        {showProjectsList && !hasNoProjects && !hasNoFilteredProjects && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              {(selection.isSelectionMode || selection.isBulkSelectionMode) ? (
                <>
                  <div className={styles.selectionHeaderLeft}>
                    <span className={styles.selectedCountText}>({totalSelected}) Selected</span>
                    <button type="button" className={styles.selectedBadge} onClick={selection.handleCloseSelection}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.5 4.1125L9.8875 3.5L7 6.3875L4.1125 3.5L3.5 4.1125L6.3875 7L3.5 9.8875L4.1125 10.5L7 7.6125L9.8875 10.5L10.5 9.8875L7.6125 7L10.5 4.1125Z" fill="#505050"/>
                      </svg>
                    </button>
                  </div>
                  <div className={styles.selectionHeaderRight}>
                    <label className={styles.selectAllLinkButton}>
                      <input type="checkbox" className={styles.selectAllCheckbox}
                        checked={selection.isAllSelected} onChange={selection.handleSelectAllClick} />
                      <span className={styles.selectAllText}>Select All ({allFilteredProjects.length})</span>
                    </label>
                    <button className={styles.deleteButtonRed} onClick={selection.handleDeleteSelected}>
                      <svg width="20" height="23" viewBox="0 0 20 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.982 4.53334H16.2461L15.7032 2.36069C15.3558 0.969984 14.1117 0 12.6797 0H7.15306C5.72093 0 4.47791 0.969984 4.12948 2.36069L3.58658 4.53334H0.849933C0.380335 4.53334 0 4.91367 0 5.38327C0 5.85287 0.380335 6.2332 0.849933 6.2332H1.19203L2.11738 19.2041C2.23318 20.8264 3.59838 22.0982 5.22609 22.0982H14.6039C16.2315 22.0982 17.5968 20.8265 17.7126 19.2041L18.638 6.2332H18.9801C19.4497 6.2332 19.83 5.85287 19.83 5.38327C19.83 4.91367 19.4497 4.53334 18.9801 4.53334H18.982ZM5.77933 2.7729C5.93762 2.14077 6.50284 1.69987 7.15409 1.69987H12.6807C13.3319 1.69987 13.8972 2.14183 14.0555 2.7729L14.4953 4.53334H5.33834L5.77818 2.7729H5.77933ZM16.0188 19.0832C15.9667 19.8205 15.3452 20.3984 14.6058 20.3984H5.22686C4.48634 20.3984 3.8659 19.8205 3.81385 19.0832L2.89592 6.23328H16.9357L16.0178 19.0832H16.0188ZM11.3326 15.5825V9.91669C11.3326 9.44709 11.7129 9.06675 12.1825 9.06675C12.6521 9.06675 13.0324 9.44709 13.0324 9.91669V15.5825C13.0324 16.0521 12.6521 16.4325 12.1825 16.4325C11.7129 16.4325 11.3326 16.0521 11.3326 15.5825ZM6.80033 15.5825V9.91669C6.80033 9.44709 7.18067 9.06675 7.65026 9.06675C8.11986 9.06675 8.5002 9.44709 8.5002 9.91669V15.5825C8.5002 16.0521 8.11986 16.4325 7.65026 16.4325C7.18067 16.4325 6.80033 16.0521 6.80033 15.5825Z" fill="white"/>
                      </svg>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.sectionHeaderTitle}>
                    <span className={styles.allProjectsTitle}>{listTitle} ({mergedItems.length})</span>
                  </div>
                  <div className={styles.sectionHeaderActions}>
                    <button className={styles.selectLinkButton} onClick={selection.handleEnableSelection}>Select</button>
                  </div>
                </>
              )}
            </div>

            <div className={styles.smallCardsGrid}>
              {getPaginatedData(mergedItems).map((item) => {
                if (item.type === 'score-single') {
                  return (
                    <SingleScoreCard key={item.key} result={item.result}
                      onViewScore={(asin, scoreData) => viewListingScore(navigate, asin, scoreData)} />
                  );
                }
                if (item.type === 'score-batch') {
                  return (
                    <BulkScoreCard key={item.key} batch={item.batch}
                      onClick={() => viewScoreBatch(navigate, item.batch)} />
                  );
                }
                if (item.type === 'deal') {
                  return (
                    <DealTagCard key={item.key} batch={item.batch}
                      onClick={() => setSelectedDealBatch(item.batch)} />
                  );
                }
                if (item.type === 'title') {
                  return (
                    <TitleUpdateCard key={item.key} project={item.project}
                      onOpen={setSelectedTitleProject} />
                  );
                }
                const project = item.project;
                if (project.projectType === 'single') {
                  const generationId = project._id;
                  const currentProjectName = getCardTitle(project);
                  return (
                    <SingleProjectCard
                      key={`all-${generationId}`}
                      generation={project}
                      cardImage={getCardImage(project)}
                      currentProjectName={currentProjectName}
                      isEditing={nameEdit.editingProjectName === generationId}
                      isDraft={isDraftItem(project)}
                      isSelected={selection.selectedProjects.includes(generationId)}
                      listingMode={getListingMode(project)}
                      editingProjectName={nameEdit.editingProjectName}
                      newProjectName={nameEdit.newProjectName}
                      setNewProjectName={nameEdit.setNewProjectName}
                      onSaveProjectName={nameEdit.handleSaveProjectName}
                      onCancelEdit={nameEdit.handleCancelEdit}
                      onStartEdit={() => { nameEdit.setEditingProjectName(generationId); nameEdit.setNewProjectName(currentProjectName); }}
                      onCardClick={() => handleCardClick(project)}
                      onSelectionChange={selection.handleProjectSelection}
                      isSelectionMode={isInSelectionMode('single')}
                      formatDate={formatDate}
                    />
                  );
                }
                const projectName = getBulkProjectName(project);
                return (
                  <BulkProjectCard
                    key={`all-bulk-${project._id}`}
                    bulkProject={project}
                    previewImage={getBulkPreviewImage(project)}
                    projectName={projectName}
                    isSelected={selection.selectedBulkProjects.includes(project._id)}
                    listingMode={getListingMode(project)}
                    editingBulkProjectName={nameEdit.editingBulkProjectName}
                    newBulkProjectName={nameEdit.newBulkProjectName}
                    setNewBulkProjectName={nameEdit.setNewBulkProjectName}
                    onSaveBulkProjectName={nameEdit.handleSaveBulkProjectName}
                    onCancelBulkProjectNameEdit={nameEdit.handleCancelBulkProjectNameEdit}
                    onStartBulkEdit={() => { nameEdit.setEditingBulkProjectName(project._id); nameEdit.setNewBulkProjectName(projectName); }}
                    isUpdatingBulkProjectName={nameEdit.isUpdatingBulkProjectName}
                    onCardClick={() => handleBulkCardClick(project)}
                    onSelectionChange={selection.handleBulkProjectSelection}
                    isSelectionMode={isInSelectionMode('bulk')}
                    formatDate={formatDate}
                  />
                );
              })}
            </div>

            <PaginationControls totalItems={mergedItems.length} currentPage={currentPage}
              itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
          </div>
        )}

        {showProjectsList && data.bulkLoading && (
          <div className={styles.loading}><FaSpinner className={styles.spinner} /></div>
        )}
        {showProjectsList && data.bulkError && !data.bulkLoading && (
          <div className={styles.error}>{data.bulkError}</div>
        )}
      </div>
    </>
  );
};

export default ManagePage;
